import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import BackButton from '../BackButton'
import { SectionItem } from '../Section'
import { makeDefaultFailedContainer } from 'components/container'
import { IntlProvider } from 'react-intl'
import { getChanges } from 'components/Customize/OrgSettings/Helpers'
import Toggle from 'components/Toggle'
import HelpingTeamMemberSelection from 'components/Customize/Online/HelpingTeamMemberSelection'
import { makeHasGate } from 'app/modules/gates/selectors'

import WelcomeMessageBuilderContainer from './components/WelcomeMessageBuilderContainer'
import MessageInputs from './components/MessageInputs'
import PreviewButtons from './components/PreviewButtons'
import { PreviewState } from './constants'
import PageTitle from '../PageTitle'

import style from './style.styl'

const NOTIFICATION_MESSAGE = 'The message settings of your Drift Widget have been updated!'

const PageDescription = () => (
  <div className="page-description">
    <span>Customize your Welcome Message to convey your own copy.</span>
    <a href="http://help.drift.com/customization/welcome-message" target="_blank">
      Learn More
    </a>
  </div>
)

const NewPageDescription = () => (
  <div className="page-description">
    <span>Customize how you greet your site visitors when they click on your Drift Widget.</span>
    <a href="https://help.drift.com/article/setting-up-a-default-greeting/" target="_blank">
      Learn More
    </a>
  </div>
)

const DELAY_IN_MS = 1000

const orNull = input => input || null

class WelcomeMessage extends Component {
  static contextTypes = {
    app: PropTypes.object,
  }

  constructor(props) {
    super(props)

    this.onToggleWelcomeMessage = this.onToggleWelcomeMessage.bind(this)
    this.onPreviewStateChanged = this.onPreviewStateChanged.bind(this)
    this.onAwayMessageChanged = this.onAwayMessageChanged.bind(this)
    this.onWelcomeMessageChanged = this.onWelcomeMessageChanged.bind(this)
    this.handleSelectUser = this.handleSelectUser.bind(this)
    this.handleUserListModeChange = this.handleUserListModeChange.bind(this)
    this.patchOrgSettingsDebounced = _.debounce(this.patchOrgSettings, DELAY_IN_MS)

    this.state = {
      previewState: PreviewState.ONLINE,
      welcomeMessage: props.orgSettings.theme.welcomeMessage,
      awayMessage: props.orgSettings.theme.awayMessage,
    }
  }

  onPreviewStateChanged(previewState) {
    return () => {
      this.setState({ previewState })
    }
  }

  onToggleWelcomeMessage(event) {
    const { orgSettings } = this.props
    const showWelcomeMessage = event.target.checked
    const updatedOrgSettings = orgSettings.setIn(['theme', 'showWelcomeMessage'], showWelcomeMessage)

    this.patchOrgSettings(updatedOrgSettings, orgSettings)
  }

  updateOrgSettings(updatedOrgSettings) {
    const { actions } = this.context.app
    actions.OrgSettingsActions.updateOrgSettings(updatedOrgSettings)
  }

  patchOrgSettings(updatedOrgSettings, orgSettings) {
    const changes = getChanges(updatedOrgSettings, orgSettings)

    const { actions } = this.context.app
    actions.DefaultEmbedActions.patchDefaultEmbed(changes, NOTIFICATION_MESSAGE)
  }

  handleUserListModeChange = userListMode => {
    const { orgSettings } = this.props
    const updatedOrgSettings = orgSettings.setIn(['theme', 'userListMode'], userListMode)

    this.patchOrgSettings(updatedOrgSettings, orgSettings)
  }

  handleSelectUser = userIds => {
    const { orgSettings } = this.props
    const updatedOrgSettings = orgSettings.setIn(['theme', 'userList'], userIds)

    this.patchOrgSettings(updatedOrgSettings, orgSettings)
  }

  onWelcomeMessageChanged(welcomeMessage, isValid) {
    if (!isValid) return

    this.setState({
      previewState: PreviewState.ONLINE,
      welcomeMessage,
    })

    const { orgSettings } = this.props
    const updatedOrgSettings = orgSettings.setIn(['theme', 'welcomeMessage'], orNull(welcomeMessage))

    this.patchOrgSettingsDebounced(updatedOrgSettings, orgSettings)
  }

  onAwayMessageChanged(awayMessage, isValid) {
    if (!isValid) return

    this.setState({
      previewState: PreviewState.AWAY,
      awayMessage,
    })

    const { orgSettings } = this.props
    const updatedOrgSettings = orgSettings.setIn(['theme', 'awayMessage'], orNull(awayMessage))

    this.patchOrgSettingsDebounced(updatedOrgSettings, orgSettings)
  }

  render() {
    const { currentUser, orgSettings, users, hasConsolidatedWelcomeMsg } = this.props
    const name = currentUser.name || orgSettings.orgName
    const theme = orgSettings.theme
    const { previewState, welcomeMessage, awayMessage } = this.state
    const message = previewState === PreviewState.ONLINE ? welcomeMessage : awayMessage

    return (
      <IntlProvider locale="en">
        <div className={style.WelcomeMessage}>
          <BackButton title="Drift Widget Settings" url="/settings/widget" />

          {!hasConsolidatedWelcomeMsg && (
            <PageTitle
              title="Welcome Message"
              description={<PageDescription />}
              className="welcome-message-page-title"
            />
          )}
          {hasConsolidatedWelcomeMsg && (
            <PageTitle
              title="Default Greeting"
              description={<NewPageDescription />}
              className="welcome-message-page-title"
            />
          )}

          <PreviewButtons previewState={previewState} onPreviewStateChanged={this.onPreviewStateChanged} />

          <div className="preview-container">
            <div className="preview">
              <WelcomeMessageBuilderContainer
                theme={theme}
                previewState={previewState}
                message={message}
                currentUser={currentUser}
                name={name}
              />
            </div>
            {!hasConsolidatedWelcomeMsg && (
              <div className="welcome-message-info">
                <Toggle
                  defaultChecked={theme.showWelcomeMessage}
                  onChange={this.onToggleWelcomeMessage}
                  className="welcome-message-toggle"
                />
                <SectionItem
                  title="Welcome Message"
                  description="Automatically show your welcome message before someone opens the chat window. The welcome message will not automatically display when the widget is offline."
                  className="info-section"
                />
              </div>
            )}
          </div>

          <MessageInputs
            welcomeMessage={welcomeMessage}
            awayMessage={awayMessage}
            onWelcomeMessageChanged={this.onWelcomeMessageChanged}
            onAwayMessageChanged={this.onAwayMessageChanged}
          />

          <div className="team-members">
            <SectionItem
              title={`Who shows on the ${hasConsolidatedWelcomeMsg ? 'default greeting' : 'Welcome Message'}`}
            />
            <HelpingTeamMemberSelection
              allUsers={users}
              userList={theme.userList}
              userListMode={theme.userListMode}
              onSelectUser={this.handleSelectUser}
              onUserListModeChange={this.handleUserListModeChange}
              validation={true}
            />
          </div>
        </div>
      </IntlProvider>
    )
  }
}

const mapStateToProps = state => ({
  hasConsolidatedWelcomeMsg: makeHasGate('has_consolidated_welcome_msg')(state),
})

const WelcomeMessageContainer = makeDefaultFailedContainer(
  connect(mapStateToProps)(withRouter(WelcomeMessage)),
  {
    listenTo: ['stores.UserStore'],
    fetch: {
      orgSettings() {
        return this.app.stores.OrgSettingsStore.getOrgSettings()
      },
      currentUser() {
        return this.app.stores.UserStore.getOrFetchCurrentUser()
      },
      users() {
        return this.app.stores.UserStore.getOrFetchUsersForCurrentUserOrg()
      },
    },
  }
)

export default WelcomeMessageContainer



// WEBPACK FOOTER //
// ./src/scripts/components/settings/ChatWidget/components/WelcomeMessage/index.js